import EventManager from '@/event-manager/event-manager';
import { GAME_WINDOW_TO_CLIENT_EVENTS } from '@/event-manager/types';
import { clearGameGlsPayload } from '@/game-client/game-gls-payload';
import {
  getGlsLaunchGeneration,
  getRecordedHandshakeLaunchEpoch,
  isGlsLaunchGenerationCurrent,
  recordHandshakeLaunchEpoch,
  releaseGlsLaunch,
} from '@/game-client/gls-launch-guard';
import {
  setClientStatus,
  setGamePayload,
  setGameStatus,
} from '@/store/actions';
import { useStore } from '@/store/useStore';
import { getGameClientFrame } from '@/utilities/getters/getGameClientFrame';
import { getIframeTargetOrigin } from '@/utilities/getters/getIframeTargetOrigin';
import { trackExperimentExposures } from '@/utilities/logger/amplitude/experiments/experimentExposure';
import {
  CLIENT_EXPERIMENT_FLAG_KEYS,
  mapExperimentsForClient,
} from '@/utilities/logger/amplitude/experiments/mapExperiments';
import {
  isJackpotGlsUrlDeliveryDeferred,
  processGlsDurationVital,
} from '@/utilities/logger/datadog/vitals';
import { VitalDurationType } from '@fanduel/casino-datadog-logger/src/types';

export const clearStoredGlsInitializeUrl = clearGameGlsPayload;

/** GL/INITIALIZE_URL was emitted for the stored payload; awaits GC/CONTEXT_RECEIVED. */
let glsInitializeUrlDeliveryInFlight = false;

/** @internal test helper */
export const resetStoredGlsInitializeUrlForTests = (): void => {
  glsInitializeUrlDeliveryInFlight = false;
  clearGameGlsPayload();
};

const completeGlsUrlDelivery = (generation: number): void => {
  glsInitializeUrlDeliveryInFlight = false;
  clearGameGlsPayload();
  releaseGlsLaunch(generation);
  processGlsDurationVital(VitalDurationType.STOP);
};

/** Payload + handshake match current launch; not blocked by jackpot work. */
const isGlsInitializeUrlDeliveryEligible = (): boolean => {
  if (isJackpotGlsUrlDeliveryDeferred()) {
    return false;
  }

  const currentEpoch = getGlsLaunchGeneration();
  const { gamePayload, gameStatus } = useStore.getState();
  const { glsInitializeUrl, handshakeLaunchEpoch } = gamePayload;

  if (glsInitializeUrl == null) {
    return false;
  }

  if (gameStatus.jackpotReconcileRunning) {
    return false;
  }

  if (handshakeLaunchEpoch !== currentEpoch) {
    return false;
  }

  return true;
};

export const markClientHandshakeReceived = (): void => {
  const epoch = getGlsLaunchGeneration();
  recordHandshakeLaunchEpoch(epoch);
  setClientStatus({ loaded: true });
  setGamePayload({
    handshakeLaunchEpoch: epoch,
  });
  attemptDeliverGlsInitializeUrl();
};

/**
 * Re-applies {@link GamePayload.handshakeLaunchEpoch} from the module record when
 * the store was reset but GC already handshook for the current launch generation.
 * `clientStatus.loaded` is set only by {@link markClientHandshakeReceived}, not here.
 */
const restoreHandshakeLaunchEpochFromRecorded = (): boolean => {
  const currentEpoch = getGlsLaunchGeneration();
  const recordedEpoch = getRecordedHandshakeLaunchEpoch();

  if (recordedEpoch !== currentEpoch) {
    return false;
  }

  if (useStore.getState().gamePayload.handshakeLaunchEpoch === currentEpoch) {
    return true;
  }

  setGamePayload({ handshakeLaunchEpoch: currentEpoch });
  return true;
};

/**
 * Restores store epoch from the module record when it matches the current launch
 * generation. No production caller — presets invalidate before reset, which clears
 * the record; handshake + {@link storeGlsInitializeUrl} own the live path.
 * Kept for unit tests and optional delivery retries.
 */
export const resyncGamePayloadHandshakeAfterStoreReset = (): void => {
  if (restoreHandshakeLaunchEpochFromRecorded()) {
    attemptDeliverGlsInitializeUrl();
  }
};

/** Writes the GLS init payload to the game slice; delivery is handshake-gated. */
export const storeGlsInitializeUrl = (
  payload: unknown,
  generation: number,
): void => {
  if (!isGlsLaunchGenerationCurrent(generation)) {
    return;
  }

  glsInitializeUrlDeliveryInFlight = false;
  setGamePayload({
    glsInitializeUrl: payload,
  });
  restoreHandshakeLaunchEpochFromRecorded();
  attemptDeliverGlsInitializeUrl();
};

/**
 * Completes delivery after GC applies the URL and posts GC/CONTEXT_RECEIVED.
 */
export const markGlsInitializeUrlContextReceived = (): void => {
  if (!glsInitializeUrlDeliveryInFlight) {
    return;
  }

  const { gamePayload } = useStore.getState();
  const { handshakeLaunchEpoch } = gamePayload;
  const currentEpoch = getGlsLaunchGeneration();

  if (handshakeLaunchEpoch !== currentEpoch) {
    glsInitializeUrlDeliveryInFlight = false;
    return;
  }

  setGameStatus({ jackpotGlsPrewarmContextReceived: true });
  completeGlsUrlDelivery(currentEpoch);
};

const emitInitializeUrl = (payload: unknown): boolean => {
  const frame = getGameClientFrame();
  if (!frame?.contentWindow) {
    return false;
  }

  const { amplitudeExperiments } = useStore.getState();
  const experiments = mapExperimentsForClient(amplitudeExperiments);
  const payloadWithExperiments =
    payload && typeof payload === 'object'
      ? { ...(payload as Record<string, unknown>), experiments }
      : { experiments };
  EventManager.emit(
    frame.contentWindow,
    'message',
    GAME_WINDOW_TO_CLIENT_EVENTS.INITIALIZE_URL,
    payloadWithExperiments,
    getIframeTargetOrigin(frame.src),
  );
  // The game-client acts on these flags from here on but cannot track exposures itself.
  trackExperimentExposures(CLIENT_EXPERIMENT_FLAG_KEYS);

  return true;
};

/**
 * Posts GL/INITIALIZE_URL when a stored payload and handshake match the current
 * launch epoch. Re-invoked by {@link useGlsInitializeUrlDelivery} when readiness
 * gates change; does not poll on an interval.
 */
export const attemptDeliverGlsInitializeUrl = (): boolean => {
  restoreHandshakeLaunchEpochFromRecorded();

  if (!isGlsInitializeUrlDeliveryEligible()) {
    return false;
  }

  if (glsInitializeUrlDeliveryInFlight) {
    return true;
  }

  const { glsInitializeUrl } = useStore.getState().gamePayload;

  if (glsInitializeUrl == null) {
    return false;
  }

  const { clientStatus } = useStore.getState();
  if (!clientStatus.loaded) {
    return false;
  }

  if (!emitInitializeUrl(glsInitializeUrl)) {
    return false;
  }

  glsInitializeUrlDeliveryInFlight = true;
  return true;
};

/** @deprecated use {@link attemptDeliverGlsInitializeUrl} */
export const tryEmitStoredGlsInitializeUrl = attemptDeliverGlsInitializeUrl;
